// =============================================================================
// Module Project - Service: Centralized Notifications
// Service centralisé pour envoyer des notifications pour toutes les actions
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import type { NotificationType, EntityType, NotificationPayload } from './notificationRepository';

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: Get actor name from user ID
// ─────────────────────────────────────────────────────────────────────────────
export async function getActorName(userId?: string): Promise<string> {
  if (!userId) return 'Quelqu\'un';
  
  const { data } = await supabase
    .from('core_profiles')
    .select('full_name, email')
    .eq('id', userId)
    .single();
  
  return data?.full_name || data?.email || 'Quelqu\'un';
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: Get member ID from user ID
// ─────────────────────────────────────────────────────────────────────────────
export async function getMemberIdFromUserId(orgId: string, userId: string): Promise<string | null> {
  const { data } = await supabase
    .from('core_organization_members')
    .select('id')
    .eq('org_id', orgId)
    .eq('user_id', userId)
    .eq('status', 'active')
    .single();
  
  return data?.id || null;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: Get user ID from member ID
// ─────────────────────────────────────────────────────────────────────────────
export async function getUserIdFromMemberId(memberId: string): Promise<string | null> {
  const { data } = await supabase
    .from('core_organization_members')
    .select('user_id')
    .eq('id', memberId)
    .single();
  
  return data?.user_id || null;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: Get task watchers
// ─────────────────────────────────────────────────────────────────────────────
export async function getTaskWatcherMemberIds(taskId: string): Promise<string[]> {
  const { data } = await supabase
    .from('project_task_watchers')
    .select('org_member_id')
    .eq('task_id', taskId);
  
  return (data || []).map(w => w.org_member_id);
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: Get task assignees
// ─────────────────────────────────────────────────────────────────────────────
export async function getTaskAssigneeMemberIds(taskId: string): Promise<string[]> {
  const { data } = await supabase
    .from('project_task_assignees')
    .select('org_member_id')
    .eq('task_id', taskId);
  
  return (data || []).map(a => a.org_member_id);
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: Get task owner member ID
// ─────────────────────────────────────────────────────────────────────────────
export async function getTaskOwnerMemberId(taskId: string): Promise<string | null> {
  const { data } = await supabase
    .from('project_tasks')
    .select('owner_member_id')
    .eq('id', taskId)
    .single();
  
  return data?.owner_member_id || null;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: Get task info
// ─────────────────────────────────────────────────────────────────────────────
export async function getTaskInfo(taskId: string): Promise<{ title: string; projectId: string } | null> {
  const { data } = await supabase
    .from('project_tasks')
    .select('title, project_id')
    .eq('id', taskId)
    .single();
  
  return data ? { title: data.title, projectId: data.project_id } : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// CORE: Create notification
// ─────────────────────────────────────────────────────────────────────────────
async function createNotification(
  orgId: string,
  projectId: string,
  recipientMemberId: string,
  type: NotificationType,
  entityType: EntityType,
  entityId: string,
  payload: NotificationPayload
): Promise<void> {
  const { error } = await supabase
    .from('project_notifications')
    .insert({
      org_id: orgId,
      project_id: projectId,
      recipient_member_id: recipientMemberId,
      type,
      entity_type: entityType,
      entity_id: entityId,
      payload: payload as unknown as Json,
    });

  if (error) {
    console.error('Error creating notification:', error);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CORE: Create notifications for multiple recipients
// ─────────────────────────────────────────────────────────────────────────────
async function createNotificationsForRecipients(
  orgId: string,
  projectId: string,
  recipientMemberIds: string[],
  type: NotificationType,
  entityType: EntityType,
  entityId: string,
  payload: NotificationPayload,
  excludeMemberId?: string
): Promise<void> {
  const recipients = excludeMemberId
    ? recipientMemberIds.filter(id => id !== excludeMemberId)
    : recipientMemberIds;

  if (recipients.length === 0) return;

  const notifications = recipients.map(recipientMemberId => ({
    org_id: orgId,
    project_id: projectId,
    recipient_member_id: recipientMemberId,
    type,
    entity_type: entityType,
    entity_id: entityId,
    payload: payload as unknown as Json,
  }));

  const { error } = await supabase
    .from('project_notifications')
    .insert(notifications);

  if (error) {
    console.error('Error creating bulk notifications:', error);
  }
}

// =============================================================================
// TASK NOTIFICATIONS
// =============================================================================

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Task assigned
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyTaskAssigned(params: {
  orgId: string;
  projectId: string;
  taskId: string;
  taskTitle: string;
  assigneeMemberId: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, taskId, taskTitle, assigneeMemberId, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  // Don't notify if you're assigning yourself
  if (actorMemberId === assigneeMemberId) return;
  
  await createNotification(orgId, projectId, assigneeMemberId, 'assign', 'task', taskId, {
    taskId,
    taskTitle,
    actorName,
    actorMemberId: actorMemberId || undefined,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Task unassigned
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyTaskUnassigned(params: {
  orgId: string;
  projectId: string;
  taskId: string;
  taskTitle: string;
  unassignedMemberId: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, taskId, taskTitle, unassignedMemberId, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  // Don't notify if you're unassigning yourself
  if (actorMemberId === unassignedMemberId) return;
  
  await createNotification(orgId, projectId, unassignedMemberId, 'unassign', 'task', taskId, {
    taskId,
    taskTitle,
    actorName,
    actorMemberId: actorMemberId || undefined,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Owner changed
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyOwnerChanged(params: {
  orgId: string;
  projectId: string;
  taskId: string;
  taskTitle: string;
  newOwnerMemberId: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, taskId, taskTitle, newOwnerMemberId, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  // Don't notify if you're setting yourself as owner
  if (actorMemberId === newOwnerMemberId) return;
  
  await createNotification(orgId, projectId, newOwnerMemberId, 'owner_change', 'task', taskId, {
    taskId,
    taskTitle,
    actorName,
    actorMemberId: actorMemberId || undefined,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Comment added (watchers + assignees + owner)
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyCommentAdded(params: {
  orgId: string;
  projectId: string;
  taskId: string;
  taskTitle: string;
  commentId: string;
  commentBody: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, taskId, taskTitle, commentId, commentBody, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  // Get all interested parties
  const [watchers, assignees, ownerId] = await Promise.all([
    getTaskWatcherMemberIds(taskId),
    getTaskAssigneeMemberIds(taskId),
    getTaskOwnerMemberId(taskId),
  ]);
  
  // Combine and deduplicate
  const allRecipients = new Set([...watchers, ...assignees]);
  if (ownerId) allRecipients.add(ownerId);
  
  await createNotificationsForRecipients(
    orgId,
    projectId,
    Array.from(allRecipients),
    'comment',
    'comment',
    commentId,
    {
      taskId,
      taskTitle,
      actorName,
      actorMemberId: actorMemberId || undefined,
      commentBody: commentBody.substring(0, 100), // Truncate for preview
    },
    actorMemberId || undefined
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Mention in comment
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyMention(params: {
  orgId: string;
  projectId: string;
  taskId: string;
  taskTitle: string;
  commentId: string;
  mentionedMemberIds: string[];
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, taskId, taskTitle, commentId, mentionedMemberIds, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  await createNotificationsForRecipients(
    orgId,
    projectId,
    mentionedMemberIds,
    'mention',
    'comment',
    commentId,
    {
      taskId,
      taskTitle,
      actorName,
      actorMemberId: actorMemberId || undefined,
    },
    actorMemberId || undefined
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Status changed (watchers + assignees + owner)
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyStatusChanged(params: {
  orgId: string;
  projectId: string;
  taskId: string;
  taskTitle: string;
  oldStatus: string;
  newStatus: string;
  isTerminal: boolean;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, taskId, taskTitle, oldStatus, newStatus, isTerminal, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  // Get all interested parties
  const [watchers, assignees, ownerId] = await Promise.all([
    getTaskWatcherMemberIds(taskId),
    getTaskAssigneeMemberIds(taskId),
    getTaskOwnerMemberId(taskId),
  ]);
  
  const allRecipients = new Set([...watchers, ...assignees]);
  if (ownerId) allRecipients.add(ownerId);
  
  const type: NotificationType = isTerminal ? 'status_terminal' : 'status_change';
  
  await createNotificationsForRecipients(
    orgId,
    projectId,
    Array.from(allRecipients),
    type,
    'task',
    taskId,
    {
      taskId,
      taskTitle,
      actorName,
      actorMemberId: actorMemberId || undefined,
      oldStatus,
      newStatus,
    },
    actorMemberId || undefined
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Attachment uploaded
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyAttachmentUploaded(params: {
  orgId: string;
  projectId: string;
  taskId: string;
  taskTitle: string;
  attachmentId: string;
  fileName: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, taskId, taskTitle, attachmentId, fileName, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  const [watchers, assignees, ownerId] = await Promise.all([
    getTaskWatcherMemberIds(taskId),
    getTaskAssigneeMemberIds(taskId),
    getTaskOwnerMemberId(taskId),
  ]);
  
  const allRecipients = new Set([...watchers, ...assignees]);
  if (ownerId) allRecipients.add(ownerId);
  
  await createNotificationsForRecipients(
    orgId,
    projectId,
    Array.from(allRecipients),
    'attachment_uploaded',
    'attachment',
    attachmentId,
    {
      taskId,
      taskTitle,
      actorName,
      actorMemberId: actorMemberId || undefined,
      fileName,
    },
    actorMemberId || undefined
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Watcher added
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyWatcherAdded(params: {
  orgId: string;
  projectId: string;
  taskId: string;
  taskTitle: string;
  watcherMemberId: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, taskId, taskTitle, watcherMemberId, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  // Don't notify if you're adding yourself as watcher
  if (actorMemberId === watcherMemberId) return;
  
  await createNotification(orgId, projectId, watcherMemberId, 'watcher_added', 'task', taskId, {
    taskId,
    taskTitle,
    actorName,
    actorMemberId: actorMemberId || undefined,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Dependency blocking
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyDependencyBlocking(params: {
  orgId: string;
  projectId: string;
  blockedTaskId: string;
  blockedTaskTitle: string;
  blockingTaskTitle: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, blockedTaskId, blockedTaskTitle, blockingTaskTitle, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  const [assignees, ownerId] = await Promise.all([
    getTaskAssigneeMemberIds(blockedTaskId),
    getTaskOwnerMemberId(blockedTaskId),
  ]);
  
  const allRecipients = new Set([...assignees]);
  if (ownerId) allRecipients.add(ownerId);
  
  await createNotificationsForRecipients(
    orgId,
    projectId,
    Array.from(allRecipients),
    'dependency_blocked',
    'dependency',
    blockedTaskId,
    {
      taskId: blockedTaskId,
      taskTitle: blockedTaskTitle,
      dependencyTaskTitle: blockingTaskTitle,
      actorName,
      actorMemberId: actorMemberId || undefined,
    },
    actorMemberId || undefined
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Due date changed
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyDueDateChanged(params: {
  orgId: string;
  projectId: string;
  taskId: string;
  taskTitle: string;
  oldDueDate: string | null;
  newDueDate: string | null;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, taskId, taskTitle, oldDueDate, newDueDate, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  const [assignees, ownerId] = await Promise.all([
    getTaskAssigneeMemberIds(taskId),
    getTaskOwnerMemberId(taskId),
  ]);
  
  const allRecipients = new Set([...assignees]);
  if (ownerId) allRecipients.add(ownerId);
  
  await createNotificationsForRecipients(
    orgId,
    projectId,
    Array.from(allRecipients),
    'due_date_changed',
    'task',
    taskId,
    {
      taskId,
      taskTitle,
      actorName,
      actorMemberId: actorMemberId || undefined,
      oldValue: oldDueDate || undefined,
      newValue: newDueDate || undefined,
      dueDate: newDueDate || undefined,
    },
    actorMemberId || undefined
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Priority changed
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyPriorityChanged(params: {
  orgId: string;
  projectId: string;
  taskId: string;
  taskTitle: string;
  oldPriority: string | null;
  newPriority: string | null;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, taskId, taskTitle, oldPriority, newPriority, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  const [assignees, ownerId] = await Promise.all([
    getTaskAssigneeMemberIds(taskId),
    getTaskOwnerMemberId(taskId),
  ]);
  
  const allRecipients = new Set([...assignees]);
  if (ownerId) allRecipients.add(ownerId);
  
  await createNotificationsForRecipients(
    orgId,
    projectId,
    Array.from(allRecipients),
    'priority_changed',
    'task',
    taskId,
    {
      taskId,
      taskTitle,
      actorName,
      actorMemberId: actorMemberId || undefined,
      oldValue: oldPriority || undefined,
      newValue: newPriority || undefined,
    },
    actorMemberId || undefined
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Milestone assigned
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyMilestoneAssigned(params: {
  orgId: string;
  projectId: string;
  taskId: string;
  taskTitle: string;
  milestoneName: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, taskId, taskTitle, milestoneName, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  const [assignees, ownerId] = await Promise.all([
    getTaskAssigneeMemberIds(taskId),
    getTaskOwnerMemberId(taskId),
  ]);
  
  const allRecipients = new Set([...assignees]);
  if (ownerId) allRecipients.add(ownerId);
  
  await createNotificationsForRecipients(
    orgId,
    projectId,
    Array.from(allRecipients),
    'milestone_assigned',
    'task',
    taskId,
    {
      taskId,
      taskTitle,
      milestoneName,
      actorName,
      actorMemberId: actorMemberId || undefined,
    },
    actorMemberId || undefined
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Subtask completed
// ─────────────────────────────────────────────────────────────────────────────
export async function notifySubtaskCompleted(params: {
  orgId: string;
  projectId: string;
  parentTaskId: string;
  parentTaskTitle: string;
  subtaskTitle: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, parentTaskId, parentTaskTitle, subtaskTitle, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  const ownerId = await getTaskOwnerMemberId(parentTaskId);
  
  if (!ownerId || ownerId === actorMemberId) return;
  
  await createNotification(orgId, projectId, ownerId, 'subtask_completed', 'task', parentTaskId, {
    taskId: parentTaskId,
    taskTitle: parentTaskTitle,
    actorName,
    actorMemberId: actorMemberId || undefined,
    newValue: subtaskTitle,
  });
}

// =============================================================================
// PROJECT NOTIFICATIONS
// =============================================================================

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Project member added
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyProjectMemberAdded(params: {
  orgId: string;
  projectId: string;
  projectName: string;
  addedMemberId: string;
  roleName: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, projectName, addedMemberId, roleName, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  // Don't notify if adding yourself
  if (actorMemberId === addedMemberId) return;
  
  await createNotification(orgId, projectId, addedMemberId, 'project_member_added', 'project', projectId, {
    projectName,
    roleName,
    actorName,
    actorMemberId: actorMemberId || undefined,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Project member removed
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyProjectMemberRemoved(params: {
  orgId: string;
  projectId: string;
  projectName: string;
  removedMemberId: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, projectName, removedMemberId, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  // Don't notify if removing yourself
  if (actorMemberId === removedMemberId) return;
  
  await createNotification(orgId, projectId, removedMemberId, 'project_member_removed', 'project', projectId, {
    projectName,
    actorName,
    actorMemberId: actorMemberId || undefined,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Project role changed
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyProjectRoleChanged(params: {
  orgId: string;
  projectId: string;
  projectName: string;
  memberId: string;
  oldRole: string;
  newRole: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, projectName, memberId, oldRole, newRole, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  const actorMemberId = actorUserId ? await getMemberIdFromUserId(orgId, actorUserId) : null;
  
  // Don't notify if changing your own role
  if (actorMemberId === memberId) return;
  
  await createNotification(orgId, projectId, memberId, 'project_role_changed', 'project', projectId, {
    projectName,
    oldValue: oldRole,
    newValue: newRole,
    roleName: newRole,
    actorName,
    actorMemberId: actorMemberId || undefined,
  });
}

// =============================================================================
// TIMESHEET NOTIFICATIONS
// =============================================================================

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Timesheet approved
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyTimesheetApproved(params: {
  orgId: string;
  projectId: string;
  recipientMemberId: string;
  periodStart: string;
  periodEnd: string;
  entryCount: number;
  totalMinutes?: number;
  entryId?: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, recipientMemberId, periodStart, periodEnd, entryCount, totalMinutes, entryId, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  
  await createNotification(orgId, projectId, recipientMemberId, 'timesheet_approved', 'timesheet', entryId || projectId, {
    actorName,
    periodStart,
    periodEnd,
    entryCount,
    totalMinutes,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Notify: Timesheet rejected
// ─────────────────────────────────────────────────────────────────────────────
export async function notifyTimesheetRejected(params: {
  orgId: string;
  projectId: string;
  recipientMemberId: string;
  periodStart: string;
  periodEnd: string;
  entryCount: number;
  rejectionReason?: string;
  entryId?: string;
  actorUserId?: string;
}): Promise<void> {
  const { orgId, projectId, recipientMemberId, periodStart, periodEnd, entryCount, rejectionReason, entryId, actorUserId } = params;
  
  const actorName = await getActorName(actorUserId);
  
  await createNotification(orgId, projectId, recipientMemberId, 'timesheet_rejected', 'timesheet', entryId || projectId, {
    actorName,
    periodStart,
    periodEnd,
    entryCount,
    rejectionReason,
  });
}

// =============================================================================
// EXPORT ALL
// =============================================================================
export const NotificationService = {
  // Task notifications
  notifyTaskAssigned,
  notifyTaskUnassigned,
  notifyOwnerChanged,
  notifyCommentAdded,
  notifyMention,
  notifyStatusChanged,
  notifyAttachmentUploaded,
  notifyWatcherAdded,
  notifyDependencyBlocking,
  notifyDueDateChanged,
  notifyPriorityChanged,
  notifyMilestoneAssigned,
  notifySubtaskCompleted,
  // Project notifications
  notifyProjectMemberAdded,
  notifyProjectMemberRemoved,
  notifyProjectRoleChanged,
  // Timesheet notifications
  notifyTimesheetApproved,
  notifyTimesheetRejected,
  // Helpers
  getActorName,
  getMemberIdFromUserId,
  getTaskInfo,
};
